// GENERATED FILE — do not edit by hand.
// Source: docs/DETECTIONS.md §7.0.1 (module options table).
// Regenerate with: npm run gen:meta   ·   verify with: npm run gen:meta -- --check

import { z } from 'zod'
import type { DetectionKey } from './detections.js'

/**
 * Per-module `options` shapes (SPEC.md §4.2).
 *
 * A module absent from the documentation table takes an empty options object.
 * `ServerConfigSchema.modules` composes these with the shared `ModuleBase`.
 */
export const MODULE_OPTION_SHAPES = {
  godmode: {
    requireServerConfirmForBan: z.boolean().default(true),
    maxHealthCap: z.number().int().default(200),
  },
  health_over_max: {
    maxHealthCap: z.number().int().default(200),
  },
  health_probe: {
    probeAmount: z.number().int().min(1).max(5).default(2),
    intervalMs: z.number().int().default(20000),
    ignoreNativeRegen: z.boolean().default(false),
  },
  armor_over_max: {
    maxArmourCap: z.number().int().min(0).max(1000).default(100),
    tolerance: z.number().int().min(0).max(50).default(1),
    autoReset: z.boolean().default(true),
    sampleIntervalMs: z.number().int().min(1000).max(30000).default(3000),
  },
  invisible: {
    minAlpha: z.number().int().min(0).max(255).default(120),
    requireBothGetters: z.boolean().default(true),
    autoReset: z.boolean().default(true),
    sampleIntervalMs: z.number().int().min(1000).max(30000).default(2500),
  },
  noclip: {
    hoverHeight: z.number().min(1).max(100).default(4.0),
    hoverVerticalVelocity: z.number().min(0.05).max(5).default(0.5),
    frozenMoveDistance: z.number().min(0.1).max(50).default(1.0),
    sampleIntervalMs: z.number().int().min(250).max(5000).default(500),
  },
  speed_hack: {
    maxOnFootSpeed: z.number().min(5).max(50).default(11.0),
    maxSwimSpeed: z.number().min(1).max(30).default(4.5),
    sampleIntervalMs: z.number().int().min(250).max(5000).default(500),
  },
  teleport: {
    onFootLimit: z.number().min(20).max(2000).default(200.0),
    inVehicleLimit: z.number().min(50).max(5000).default(600.0),
    speedFactor: z.number().min(1).max(20).default(4.0),
    extraMeters: z.number().min(0).max(500).default(35.0),
    sampleIntervalMs: z.number().int().min(500).max(10000).default(1000),
  },
  spectate: {
    allowWithAdminAce: z.boolean().default(true),
    sampleIntervalMs: z.number().int().min(1000).max(30000).default(2500),
  },
  freecam: {
    clientCamDistance: z.number().min(10).max(500).default(55.0),
    ignoreInFlyingVehicle: z.boolean().default(true),
    sampleIntervalMs: z.number().int().min(1000).max(30000).default(2000),
  },
  super_jump: {
    jumpVelocityZ: z.number().min(3).max(50).default(8.0),
    serverSamples: z.number().int().min(1).max(10).default(3),
    sampleIntervalMs: z.number().int().min(250).max(5000).default(500),
  },
  infinite_stamina: {
    minSprintSpeed: z.number().min(3).max(15).default(6.5),
    staminaFloor: z.number().min(0).max(100).default(99.5),
    sampleIntervalMs: z.number().int().min(500).max(10000).default(1000),
  },
  night_vision: {
    allowInHeli: z.boolean().default(true),
    allowInPlane: z.boolean().default(true),
    autoReset: z.boolean().default(true),
    sampleIntervalMs: z.number().int().min(1000).max(30000).default(3000),
  },
  blacklisted_ped: {
    autoResetModel: z.boolean().default(false),
    sampleIntervalMs: z.number().int().min(1000).max(30000).default(5000),
  },
  blacklisted_weapon: {
    autoRemove: z.boolean().default(true),
    sampleIntervalMs: z.number().int().min(1000).max(30000).default(2500),
  },
  weapon_gained: {
    useFrameworkInventory: z.boolean().default(true),
    spawnGraceMs: z.number().int().min(0).max(120000).default(30000),
    sampleIntervalMs: z.number().int().min(1000).max(30000).default(2500),
  },
  infinite_ammo: {
    consecutiveFrames: z.number().int().min(4).max(60).default(12),
    enforceClipOff: z.boolean().default(true),
    checkAmmoOverMax: z.boolean().default(true),
  },
  explosive_ammo: {
    sampleIntervalMs: z.number().int().min(500).max(10000).default(1000),
  },
  damage_modifier: {
    maxWeaponDamage: z.number().min(0.1).max(10).default(1.0),
    maxMeleeDamage: z.number().min(0.1).max(10).default(1.0),
    minWeaponDefense: z.number().min(0.1).max(10).default(1.0),
    maxVehicleDamage: z.number().min(0.1).max(10).default(1.0),
    tolerance: z.number().min(0).max(1).default(0.01),
    sampleIntervalMs: z.number().int().min(1000).max(30000).default(3000),
  },
  weapon_damage_meta: {
    tolerancePercent: z.number().min(0).max(100).default(5),
    baselineFromFirstSample: z.boolean().default(true),
    sampleIntervalMs: z.number().int().min(1000).max(60000).default(5000),
  },
  silent_aim: {
    maxAngleDeg: z.number().min(5).max(90).default(35),
    minImpactDistance: z.number().min(0).max(200).default(5),
    ignoreInVehicle: z.boolean().default(true),
    ignoredWeapons: z.array(z.number().int()).max(64).default([]),
  },
  shot_cadence: {
    minShotGapMs: z.number().int().min(20).max(2000).default(55),
    consecutiveShots: z.number().int().min(2).max(30).default(5),
    ignoredWeapons: z.array(z.number().int()).max(64).default([]),
  },
  no_recoil: {
    consecutiveShots: z.number().int().min(3).max(50).default(8),
    minPitchKickDeg: z.number().min(0.001).max(5).default(0.05),
    ignoredWeapons: z.array(z.number().int()).max(64).default([]),
  },
  vehicle_modifier: {
    maxTopSpeedModifier: z.number().min(0).max(20).default(2.0),
    maxCheatPowerIncrease: z.number().min(0).max(20).default(2.0),
    minGravityAmount: z.number().min(0).max(50).default(4.0),
    sampleIntervalMs: z.number().int().min(1000).max(30000).default(2500),
  },
  vehicle_speed: {
    speedFactor: z.number().min(1).max(5).default(1.35),
    extraSpeed: z.number().min(0).max(100).default(10.0),
    ignoreInAir: z.boolean().default(true),
    sampleIntervalMs: z.number().int().min(1000).max(30000).default(2000),
  },
  vehicle_handling: {
    fields: z.array(z.string()).max(16).default(['fInitialDriveMaxFlatVel','fInitialDriveForce','fMass','fDriveInertia']),
    factor: z.number().min(1).max(10).default(1.5),
    sampleIntervalMs: z.number().int().min(1000).max(60000).default(5000),
  },
  vehicle_godmode: {
    requireImpact: z.boolean().default(true),
    impactWindowMs: z.number().int().min(1000).max(120000).default(20000),
    sampleIntervalMs: z.number().int().min(1000).max(30000).default(3000),
  },
  vehicle_invisible: {
    minAlpha: z.number().int().min(0).max(255).default(120),
    sampleIntervalMs: z.number().int().min(1000).max(30000).default(3000),
  },
  vehicle_noclip: {
    sampleIntervalMs: z.number().int().min(1000).max(30000).default(2000),
  },
  vehicle_gravity: {
    defaultGravity: z.number().min(0).max(100).default(9.8),
    tolerance: z.number().min(0).max(10).default(0.5),
    sampleIntervalMs: z.number().int().min(1000).max(30000).default(3000),
  },
  player_blips: {
    maxUnknownPlayerBlips: z.number().int().min(0).max(64).default(0),
    blipFloodDelta: z.number().int().min(5).max(500).default(60),
    autoRemove: z.boolean().default(false),
    sampleIntervalMs: z.number().int().min(1000).max(60000).default(5000),
  },
  entity_spam_client: {
    radius: z.number().min(5).max(500).default(50.0),
    newEntitiesPerSample: z.number().int().min(1).max(200).default(8),
    sampleIntervalMs: z.number().int().min(1000).max(30000).default(2000),
  },
  menu_texture: {
    expectedResolutionX: z.number().min(0).max(8192).default(4.0),
    sampleIntervalMs: z.number().int().min(5000).max(120000).default(15000),
  },
  menu_key: {
    controls: z.array(z.number().int()).max(32).default([121,178,214,288,289,344]),
    correlationWindowMs: z.number().int().min(1000).max(30000).default(3000),
    evidenceWeight: z.number().int().min(0).max(50).default(2),
  },
  resource_injection: {
    ignoreFirstMs: z.number().int().min(0).max(120000).default(10000),
    requireServerDiff: z.boolean().default(true),
    sampleIntervalMs: z.number().int().min(1000).max(60000).default(5000),
  },
  resource_stopped: {
    requireProbe: z.boolean().default(false),
    sampleIntervalMs: z.number().int().min(1000).max(60000).default(5000),
  },
  client_integrity: {
    files: z.array(z.string()).max(32).default(['client/init.lua','client/report.lua','client/heartbeat.lua','shared/strike.lua']),
    sampleIntervalMs: z.number().int().min(10000).max(600000).default(60000),
  },
  client_honeypot: {
    evidenceArgBytes: z.number().int().min(0).max(4096).default(512),
  },
  heartbeat_missing: {
    pingPauseThreshold: z.number().int().min(100).max(2000).default(500),
    lastMsgPauseMs: z.number().int().min(1000).max(60000).default(10000),
    useProbe: z.boolean().default(true),
  },
  honeypot_event: {
    evidenceArgBytes: z.number().int().min(0).max(4096).default(512),
  },
  honeypot_export: {
    evidenceArgBytes: z.number().int().min(0).max(4096).default(512),
  },
  honeypot_command: {
    evidenceArgBytes: z.number().int().min(0).max(4096).default(512),
  },
  explosion_spam: {
    maxPerWindow: z.number().int().min(1).max(100).default(5),
    cancelEvent: z.boolean().default(true),
    ignoreExplosiveWeaponHolder: z.boolean().default(true),
  },
  explosion_blacklist: {
    cancelEvent: z.boolean().default(true),
    maxDamageScale: z.number().min(0).max(100).default(3.0),
  },
  weapon_damage_event: {
    maxWeaponDamage: z.number().int().min(0).max(10000).default(250),
    allowOverrideDefaultDamage: z.boolean().default(false),
    requireWeaponOwnership: z.boolean().default(true),
    maxDamageDistance: z.number().min(0).max(2000).default(250.0),
    cancelEvent: z.boolean().default(true),
  },
  give_weapon_event: {
    allowOwnPed: z.boolean().default(true),
    maxPerWindow: z.number().int().min(1).max(100).default(6),
    cancelEvent: z.boolean().default(false),
  },
  remove_weapon_event: {
    allowOwnPed: z.boolean().default(true),
    cancelEvent: z.boolean().default(true),
  },
  server_headshot_ratio: {
    minHits: z.number().int().min(10).max(500).default(40),
    maxHeadshotRatio: z.number().min(0.1).max(1).default(0.62),
    windowSec: z.number().int().min(60).max(86400).default(1800),
    minDistance: z.number().min(0).max(200).default(8),
  },
  magic_bullet: {
    maxVictimsPerShot: z.number().int().min(1).max(16).default(2),
    cancelEvent: z.boolean().default(true),
  },
  fire_spam: {
    maxFiresPerWindow: z.number().int().min(1).max(500).default(20),
    cancelEvent: z.boolean().default(true),
  },
  clear_ped_tasks: {
    onlyPlayerPeds: z.boolean().default(true),
    maxPerWindow: z.number().int().min(1).max(100).default(3),
    cancelEvent: z.boolean().default(true),
  },
  projectile_spam: {
    maxPerWindow: z.number().int().min(1).max(500).default(12),
    cancelEvent: z.boolean().default(false),
  },
  ptfx_blacklist: {
    maxPerWindow: z.number().int().min(1).max(500).default(30),
    cancelEvent: z.boolean().default(true),
  },
  entity_spawn_rate: {
    maxVehiclesPerWindow: z.number().int().min(1).max(200).default(10),
    maxPedsPerWindow: z.number().int().min(1).max(500).default(30),
    maxObjectsPerWindow: z.number().int().min(1).max(500).default(30),
    cancelEvent: z.boolean().default(true),
  },
  entity_blacklisted_model: {
    checkVehicles: z.boolean().default(true),
    checkPeds: z.boolean().default(true),
    checkObjects: z.boolean().default(true),
    cancelEvent: z.boolean().default(true),
  },
  entity_unauthorized_script: {
    ignoreEmptyScript: z.boolean().default(true),
    cancelEvent: z.boolean().default(false),
  },
  server_godmode: {
    maxHealthCap: z.number().int().default(200),
    checkArmour: z.boolean().default(true),
    pollIntervalMs: z.number().int().min(1000).max(60000).default(5000),
  },
  server_super_jump: {
    pollIntervalMs: z.number().int().min(1000).max(60000).default(2000),
  },
  server_freecam: {
    distance: z.number().min(10).max(500).default(50.0),
    useFreeCamNative: z.boolean().default(true),
    pollIntervalMs: z.number().int().min(1000).max(60000).default(5000),
  },
  server_teleport: {
    onFootLimit: z.number().min(20).max(5000).default(250.0),
    inVehicleLimit: z.number().min(50).max(10000).default(700.0),
    pingToleranceMs: z.number().int().min(0).max(5000).default(500),
    pollIntervalMs: z.number().int().min(500).max(60000).default(1000),
  },
  server_speed: {
    maxOnFootSpeed: z.number().min(5).max(80).default(12.0),
    pollIntervalMs: z.number().int().min(500).max(60000).default(1000),
  },
  chat_spam: {
    maxMessages: z.number().int().min(1).max(50).default(5),
    minGapMs: z.number().int().min(0).max(10000).default(500),
    maxLength: z.number().int().min(16).max(1024).default(256),
    cancelEvent: z.boolean().default(true),
  },
  chat_blacklisted_string: {
    matchMode: z.enum(['substring','word']).default('substring'),
    cancelEvent: z.boolean().default(true),
  },
  event_spam: {
    maxEventsPerWindow: z.number().int().min(1).max(500).default(60),
    maxPerEventName: z.number().int().min(1).max(500).default(20),
    includeRateLimitedReports: z.boolean().default(true),
  },
  blacklisted_event: {
    matchStrings: z.boolean().default(true),
    cancelEvent: z.boolean().default(true),
  },
  safe_event_invalid: {
    allowPreviousBucket: z.boolean().default(true),
    strictBan: z.boolean().default(false),
  },
  event_rule: {
    maxRulesEvaluatedPerEvent: z.number().int().min(1).max(200).default(50),
  },
  resource_list_mismatch: {
    ignoreFirstMs: z.number().int().min(0).max(120000).default(15000),
    maxMissing: z.number().int().min(0).max(100).default(0),
    maxUnknown: z.number().int().min(0).max(100).default(0),
    checkRegisteredCommands: z.boolean().default(true),
  },
  connect_offline: {
    maxLogsPerMinute: z.number().int().min(1).max(600).default(30),
  },
  custom: {
    maxEvidenceBytes: z.number().int().min(256).max(8192).default(4096),
    allowFromResources: z.array(z.string()).max(64).default([]),
  },
  connect_ban_evasion: {
    matchTokens: z.boolean().default(true),
    matchIp: z.boolean().default(false),
    minMatchingTokens: z.number().int().min(1).max(8).default(2),
    lookbackDays: z.number().int().min(1).max(3650).default(90),
    autoBan: z.boolean().default(false),
  },
  connect_missing_identifier: {
    allowWhenWhitelisted: z.boolean().default(true),
  },
  connect_vpn: {
    providerTimeoutMs: z.number().int().min(500).max(10000).default(2500),
    failOpen: z.boolean().default(true),
    blockHosting: z.boolean().default(true),
  },
  connect_name_filter: {
    caseInsensitive: z.boolean().default(true),
    normalizeUnicode: z.boolean().default(true),
    maxNameLength: z.number().int().min(1).max(128).default(32),
  },
  connect_low_trust: {
    includeWhitelisted: z.boolean().default(false),
  },
} as const satisfies Record<DetectionKey, z.ZodRawShape>
